import { LinearGradient } from 'expo-linear-gradient';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { StyleProp, TouchableOpacity, ViewStyle } from 'react-native';
import {
  TapGestureHandler,
  TapGestureHandlerGestureEvent,
} from 'react-native-gesture-handler';
import Animated, {
  Easing,
  interpolate,
  interpolateColor,
  runOnJS,
  useAnimatedGestureHandler,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { SafeHaptics } from '../../utils/haptics';

type Size = 'large' | 'medium' | 'small';

type ColorValue = string | [string, string];

const kDefaultSize: Size = 'large';

export type SizeMetric = {
  thumb: number;
  margin: number;
  borderRadius: number;
};

type Props = {
  on?: boolean;
  size?: Size | SizeMetric;
  rounded?: boolean;
  borderless?: boolean;
  style?: StyleProp<ViewStyle>;
  theme?: SwitchThemeType | SwitchThemeProps;
  onChange?: (on: boolean) => void;
};

const SizeMetricMap: {
  [p in Size]: { thumb: number; margin: number; borderRadius: number };
} = {
  small: { thumb: 24, margin: 1, borderRadius: 3 },
  medium: { thumb: 32, margin: 1.5, borderRadius: 5 },
  large: { thumb: 41, margin: 2, borderRadius: 8 },
};

type SwitchThemeType =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'lime'
  | 'isoStandard';

type SwitchThemeProps = {
  activeTrackColor: ColorValue;
  inactiveTrackColor: ColorValue;
  trackGradientDirection?: 'horizontal' | 'vertical';
  activeBorderColor: ColorValue;
  inactiveBorderColor: ColorValue;
  activeThumbColor: string;
  inactiveThumbColor: string;
};

export const kDefaultSwitchTheme: SwitchThemeProps = {
  activeBorderColor: ['#BFBFBF', '#9E1068'],
  inactiveBorderColor: ['#BFBFBF', '#9E1068'],
  activeTrackColor: ['#FC3440', '#E462EB'],
  inactiveTrackColor: '#f5f5f5',
  trackGradientDirection: 'horizontal',
  activeThumbColor: '#ffffff',
  inactiveThumbColor: '#ffffff',
};

export const kPrimarySwitchTheme: SwitchThemeProps = {
  activeBorderColor: '#207FE7',
  inactiveBorderColor: '#BFBFBF',
  activeTrackColor: '#207FE7',
  inactiveTrackColor: '#F5F5F5',
  activeThumbColor: '#ffffff',
  inactiveThumbColor: '#ffffff',
};

export const kSecondarySwitchTheme: SwitchThemeProps = {
  activeBorderColor: 'rgba(130, 207, 46, 1)',
  inactiveBorderColor: 'rgba(185, 185, 185, 0.4)',
  activeTrackColor: 'rgba(130, 207, 46, 1)',
  inactiveTrackColor: 'rgba(185, 185, 185, 0.4)',
  activeThumbColor: '#ffffff',
  inactiveThumbColor: '#ffffff',
};

export const kLimeSwitchTheme: SwitchThemeProps = {
  activeBorderColor: '#CF0',
  inactiveBorderColor: 'rgba(185, 185, 185, 0.4)',
  activeTrackColor: '#CF0',
  inactiveTrackColor: 'rgba(185, 185, 185, 0.4)',
  activeThumbColor: '#ffffff',
  inactiveThumbColor: '#ffffff',
};

export const kIosStandardSwitchTheme: SwitchThemeProps = {
  activeBorderColor: '#34C759',
  inactiveBorderColor: '#E5E5EA',
  activeTrackColor: '#34C759',
  inactiveTrackColor: '#E5E5EA',
  activeThumbColor: '#ffffff',
  inactiveThumbColor: '#ffffff',
};

const predefinedSwitchThemeMap: { [p in SwitchThemeType]: SwitchThemeProps } = {
  default: kDefaultSwitchTheme,
  primary: kPrimarySwitchTheme,
  secondary: kSecondarySwitchTheme,
  lime: kLimeSwitchTheme,
  isoStandard: kIosStandardSwitchTheme,
};

const colorValueToColorStops = (value: ColorValue) =>
  typeof value === 'string' ? [value, value] : value;

export const SwitchControl = memo((props: Props) => {
  const {
    on,
    size = kDefaultSize,
    borderless,
    rounded,
    theme = kDefaultSwitchTheme,
    onChange,
  } = props;
  const {
    thumb: thumbSize,
    margin,
    borderRadius,
  } = typeof size === 'string' ? SizeMetricMap[size] : size;
  const trackSize = thumbSize * 2 + margin * 4 + 2;
  const trackHeight = thumbSize + 2 * margin + 2;
  const thumbOffsetMin = margin;
  const thumbOffsetMax = trackSize - margin - thumbSize - 1;
  const thumbOffset = useSharedValue(on ? thumbOffsetMax : thumbOffsetMin);
  const trackCornerRadius = rounded ? 1000 : borderRadius;
  const thumbCornerRadius = rounded ? 1000 : borderRadius - margin;

  const {
    activeBorderColor,
    inactiveBorderColor,
    activeTrackColor,
    inactiveTrackColor,
    activeThumbColor,
    inactiveThumbColor,
    trackGradientDirection = 'horizontal',
  } = typeof theme === 'string' ? predefinedSwitchThemeMap[theme] : theme;

  useEffect(() => {
    const targetOffset = on ? thumbOffsetMax : thumbOffsetMin;
    thumbOffset.value = withTiming(targetOffset, {
      duration: 150,
      easing: Easing.inOut(Easing.linear),
    });
  }, [on, thumbOffset, thumbOffsetMax, thumbOffsetMin]);

  const vibrate = useCallback(() => {
    SafeHaptics.selection();
  }, []);

  const onTapGestureEvent =
    useAnimatedGestureHandler<TapGestureHandlerGestureEvent>({
      onActive() {
        runOnJS(vibrate)();
        if (onChange) {
          runOnJS(onChange)(!on);
        }
        const targetOffset = on ? thumbOffsetMin : thumbOffsetMax;
        thumbOffset.value = withTiming(targetOffset, {
          duration: 150,
          easing: Easing.inOut(Easing.linear),
        });
      },
    });

  const borderColors = useMemo(
    () => colorValueToColorStops(on ? activeBorderColor : inactiveBorderColor),
    [on, activeBorderColor, inactiveBorderColor],
  );

  const activeTrackColors = useMemo(
    () => colorValueToColorStops(activeTrackColor),
    [activeTrackColor],
  );

  const inactiveTrackColors = useMemo(
    () => colorValueToColorStops(inactiveTrackColor),
    [inactiveTrackColor],
  );

  const activeTrackGradientProps = useMemo(
    () => ({
      colors: activeTrackColors,
      locations: [0, 1],
      start: { x: 0, y: 0 },
      end:
        trackGradientDirection === 'horizontal'
          ? { x: 1, y: 0 }
          : { x: 0, y: 1 },
    }),
    [activeTrackColors, trackGradientDirection],
  );

  const inactiveTrackGradientProps = useMemo(
    () => ({
      colors: inactiveTrackColors,
      locations: [0, 1],
      start: { x: 0, y: 0 },
      end:
        trackGradientDirection === 'horizontal'
          ? { x: 1, y: 0 }
          : { x: 0, y: 1 },
    }),
    [inactiveTrackColors, trackGradientDirection],
  );

  const containerStyle = useAnimatedStyle(() => {
    if (borderless) return {};
    const color = interpolateColor(
      thumbOffset.value,
      [thumbOffsetMin, thumbOffsetMax],
      borderColors,
    );
    return {
      borderColor: color,
    };
  });

  const thumbStyle = useAnimatedStyle(() => {
    const color = interpolateColor(
      thumbOffset.value,
      [thumbOffsetMin, thumbOffsetMax],
      [inactiveThumbColor, activeThumbColor],
    );
    return {
      backgroundColor: color,
      transform: [
        {
          translateX: thumbOffset.value,
        },
      ],
    };
  });

  const trackStyle = useAnimatedStyle(() => {
    const translateX = interpolate(
      thumbOffset.value,
      [thumbOffsetMin, thumbOffsetMax],
      [-trackSize, 0],
    );
    return {
      transform: [
        {
          translateX,
        },
      ],
    };
  });

  return (
    <TouchableOpacity
      style={props.style}
      css={`
        overflow: hidden;
      `}
    >
      <TapGestureHandler onGestureEvent={onTapGestureEvent}>
        <Animated.View
          css={`
            border-radius: ${trackCornerRadius}px;
            overflow: hidden;
            width: ${trackSize}px;
            height: ${trackHeight}px;
            border-width: 1px;
            border-style: solid;
          `}
          style={[containerStyle]}
        >
          {/* track */}
          <Animated.View
            style={[trackStyle]}
            css={`
              position: absolute;
              left: 0;
              top: 0;
              width: ${trackSize * 2}px;
              height: ${trackHeight}px;
            `}
          >
            <LinearGradient
              {...activeTrackGradientProps}
              css={`
                position: absolute;
                left: 0;
                top: 0;
                width: ${trackSize}px;
                height: ${trackHeight}px;
              `}
            />
            <LinearGradient
              {...inactiveTrackGradientProps}
              css={`
                position: absolute;
                left: ${trackSize}px;
                top: 0;
                width: ${trackSize}px;
                height: ${trackHeight}px;
              `}
            />
          </Animated.View>
          {/* thumb */}
          <Animated.View
            style={[thumbStyle]}
            css={`
              width: ${thumbSize}px;
              height: ${thumbSize}px;
              margin: ${margin}px 0;
              border-radius: ${thumbCornerRadius}px;
              box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.15);
            `}
          />
        </Animated.View>
      </TapGestureHandler>
    </TouchableOpacity>
  );
});
